import { useMemo } from "react";

import { templatePaginatedSearchResultsContentfulRelayRefetchableFragment$key } from "@/__generated_contentful__/templatePaginatedSearchResultsContentfulRelayRefetchableFragment.graphql";

export type CategorySearchSuggestion = {
    type: "category";
    id: string;
    name?: string | null;
    description?: string | null;
};

import {
    CategoryKey,
    CategoryTemplateOrder
} from "../../../config/category-template-order";
import { useQueryLocale } from "../../hooks/use-query-locale";

import {
    parseSearchQuery,
    TemplateInitialSearchResults
} from "./template-initial-search-results-contentful-relay";
import { TemplatePaginatedSearchResults } from "./template-paginated-search-results-contentful-relay";

export type TemplateCategorySearchResultsProps = {
    preloadedSearchResults?: templatePaginatedSearchResultsContentfulRelayRefetchableFragment$key | null;
    searchTerm: CategorySearchSuggestion;
    /**
     * A unique key to identify the search term.
     * This is required to force a new instance
     * of `TemplatePaginatedSearchResults`
     * when the search term changes.
     */
    searchKey: string;
};

export const TemplateCategorySearchResults = ({
    preloadedSearchResults,
    searchTerm,
    searchKey
}: TemplateCategorySearchResultsProps) => {
    const locale = useQueryLocale();
    const searchQuery = useMemo(
        () => parseSearchQuery(searchTerm),
        [searchTerm]
    );

    const sortLookup =
        CategoryTemplateOrder[searchTerm.id as CategoryKey] ?? "name";

    return preloadedSearchResults ? (
        <TemplatePaginatedSearchResults
            key={searchKey}
            initialSearchResults={preloadedSearchResults}
            urlSlugs={searchQuery.whereClause?.["urlSlugs"] ?? []}
            templateIds={searchQuery.whereClause?.["templateIds"] ?? []}
            categoryUrlSlugs={
                searchQuery.whereClause?.["categoryUrlSlugs"] ?? []
            }
            limit={searchQuery.first}
            skip={0}
            locale={locale}
            sortLookup={sortLookup}
            wasPreloaded={true}
        />
    ) : (
        <TemplateInitialSearchResults
            searchKey={searchKey}
            urlSlugs={searchQuery.whereClause?.["urlSlugs"] ?? []}
            templateIds={searchQuery.whereClause?.["templateIds"] ?? []}
            categoryUrlSlugs={
                searchQuery.whereClause?.["categoryUrlSlugs"] ?? []
            }
            limit={searchQuery.first}
            skip={0}
            locale={locale}
            sortLookup={sortLookup}
        />
    );
};
